import type { DataTypeLocator } from '@helo/cms'

import { Options } from '../Options'

export const formatEmptyOptions = Options.asRecord(
  Options.createOption({
    key: 'emptyFormat',
    inputKind: 'select',
    selectChoices: [
      { value: 'hyphen', label: 'Hyphen' },
      { value: 'empty', label: 'Empty' },
      { value: 'stringify', label: 'Stringify' },
    ],
    label: 'Empty format',
    description: 'How absent values are written.',
    category: 'Format',
  }),
)

export const formatTextOptions = Options.asRecord(
  Options.createOption({
    key: 'textCase',
    inputKind: 'select',
    selectChoices: [
      { value: 'all', label: 'All' },
      { value: 'word', label: 'Word' },
      { value: 'none', label: 'None' },
    ],
    label: 'Text case',
    description: 'Letter-case transform applied to formatted text.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'textPrefix',
    inputKind: 'text',
    label: 'Text prefix',
    description: 'Text prepended to the formatted value.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'textSuffix',
    inputKind: 'text',
    label: 'Text suffix',
    description: 'Text appended to the formatted value.',
    category: 'Format',
  }),
)

export const formatNumberOptions = Options.asRecord(
  Options.createOption({
    key: 'numberUnit',
    inputKind: 'select',
    selectChoices: [
      { value: 'number', label: 'Number' },
      { value: 'percentage', label: 'Percentage' },
      { value: 'currency', label: 'Currency' },
      { value: 'years', label: 'Years' },
      { value: 'months', label: 'Months' },
      { value: 'days', label: 'Days' },
      { value: 'hours', label: 'Hours' },
      { value: 'minutes', label: 'Minutes' },
      { value: 'seconds', label: 'Seconds' },
    ],
    label: 'Number unit',
    description: 'Unit used when formatting a number.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'numberFormat',
    inputKind: 'select',
    selectChoices: [
      { value: 'standard', label: 'Standard' },
      { value: 'raw', label: 'Raw' },
      { value: 'abbreviated', label: 'Abbreviated' },
    ],
    label: 'Number format',
    description:
      'How a number is written: locale grouping, digits only, or compact K/M/B/T.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'numberPrecision',
    inputKind: 'number',
    min: 0,
    max: 8,
    step: 1,
    label: 'Number precision',
    description: 'Fraction digits used when formatting a number.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'numberLocale',
    inputKind: 'text',
    label: 'Number locale',
    description: 'Locale used when formatting a number.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'numberRoundingMode',
    inputKind: 'select',
    selectChoices: [
      { value: 'trunc', label: 'Trunc' },
      { value: 'floor', label: 'Floor' },
      { value: 'ceil', label: 'Ceil' },
    ],
    label: 'Number rounding',
    description: 'Rounding mode used when formatting a number.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'numberCurrency',
    inputKind: 'text',
    label: 'Number currency',
    description: 'Currency code used when the number unit is currency.',
    category: 'Format',
  }),
)

export const formatDateOptions = Options.asRecord(
  Options.createOption({
    key: 'dateFormat',
    inputKind: 'text',
    label: 'Date format',
    description:
      'Named preset (iso, short, medium, long) or a custom date-fns pattern.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'dateGranularity',
    inputKind: 'select',
    defaultValue: 'day',
    selectChoices: [
      { value: 'second', label: 'Second' },
      { value: 'minute', label: 'Minute' },
      { value: 'hour', label: 'Hour' },
      { value: 'day', label: 'Day' },
      { value: 'week', label: 'Week' },
      { value: 'month', label: 'Month' },
      { value: 'year', label: 'Year' },
    ],
    label: 'Date granularity',
    description:
      'Finest calendar unit rendered when formatting a date. Defaults to day.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'dateIncludeTime',
    inputKind: 'boolean',
    label: 'Include time',
    description: 'Include the time portion when formatting a date.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'dateRelativeUnit',
    inputKind: 'select',
    selectChoices: [
      { value: 'hours', label: 'Hours' },
      { value: 'days', label: 'Days' },
      { value: 'weeks', label: 'Weeks' },
      { value: 'months', label: 'Months' },
      { value: 'adaptive', label: 'Adaptive' },
      { value: 'since', label: 'Since' },
      { value: 'until', label: 'Until' },
    ],
    label: 'Relative date unit',
    description: 'Unit used when formatting a relative date.',
    category: 'Format',
  }),
)

export const formatCollectionOptions = Options.asRecord(
  Options.createOption({
    key: 'asTextSeparator',
    inputKind: 'select',
    selectChoices: [
      { value: 'pipe', label: 'Pipe' },
      { value: 'comma', label: 'Comma' },
      { value: 'newline', label: 'Newline' },
      { value: 'none', label: 'None' },
    ],
    label: 'As-text separator',
    description: 'Join separator used when rendering a list as text.',
    category: 'Format',
  }),
  Options.createOption({
    key: 'asTextIncludeFieldLabels',
    inputKind: 'boolean',
    label: 'Include field labels',
    description: 'Include field labels when rendering an object as text.',
    category: 'Format',
  }),
)

const dataTypeLocatorOfType = Options.scalar<DataTypeLocator>('DataTypeLocator')

export const formatOptions = {
  ...formatEmptyOptions,
  ...formatCollectionOptions,
  ...formatTextOptions,
  ...formatNumberOptions,
  ...formatDateOptions,
  primaryValueLocator: Options.createOption({
    key: 'primaryValueLocator',
    inputKind: 'data_type_locator',
    ofType: dataTypeLocatorOfType,
    label: 'Primary value',
    description:
      'Locator selecting a scalar stand-in when formatting an object.',
    category: 'Data',
  }),
}

export type FormatOptions = Options.TypeOf<typeof formatOptions>
